/**
 * src/pages/admin/AdminRatingStats.jsx
 *
 * Founder stats for Scrimly Ratings (features/launch-readiness/, Wireframe
 * screen 3): ratings, clubs, what needs attention, and daily visit totals.
 *
 * Reads only through the founder-gated ratingsAdminStats callable, which
 * returns counts and club names: never phone numbers, account IDs, author
 * keys or comment text. Visit totals come from the nightly
 * ratingsVisitsRollup; nothing on the public pages tracks visitors.
 *
 * English only (admin path).
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { usePlatformClaims } from '@/hooks/usePlatformClaims';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

const fns = () => getFunctions(undefined, 'us-central1');
const call = async (name, data) => (await httpsCallable(fns(), name)(data || {})).data;

const PAGE_ROWS = [
  ['home', 'Home'],
  ['county', 'County pages'],
  ['club', 'Club pages'],
  ['search', 'Search'],
  ['rate', 'Rate flow'],
  ['me', 'My ratings'],
  ['legal', 'Legal pages'],
];
const HELD_LABELS = { many_clubs: 'many clubs', anomaly: 'burst flag', club_pending: 'club pending', founder: 'held by you' };

function Card({ title, children }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white px-5 py-4">
      <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-3">{title}</h2>
      {children}
    </div>
  );
}

function Row({ label, value, warn }) {
  return (
    <div className="flex items-baseline justify-between py-1 text-sm">
      <span className="text-zinc-600">{label}</span>
      <span className={`font-semibold tabular-nums ${warn ? 'text-amber-700' : 'text-zinc-900'}`}>{value ?? '–'}</span>
    </div>
  );
}

// Visit sources and campaign tags (features/visit-sources/ W-1).
function CountTable({ rows, yesterdayLabel, weekLabel, heading = 'Source' }) {
  return (
    <div className="grid grid-cols-[1fr_auto_auto] gap-x-6 text-sm">
      <span className="text-xs uppercase tracking-wider text-zinc-400 py-1">{heading}</span>
      <span className="text-xs uppercase tracking-wider text-zinc-400 py-1 text-right">{yesterdayLabel}</span>
      <span className="text-xs uppercase tracking-wider text-zinc-400 py-1 text-right">{weekLabel}</span>
      {rows.map((r) => (
        <div key={r.key} className="contents">
          <span className="text-zinc-600 py-1">{r.label}</span>
          <span className="text-right tabular-nums font-semibold py-1">{r.yesterday}</span>
          <span className="text-right tabular-nums py-1">{r.week}</span>
        </div>
      ))}
    </div>
  );
}

// Bing and other search engines show as one row; Facebook, direct and
// Google always show so a zero is visible.
const SOURCE_ROWS = [
  ['facebook', 'Facebook', ['facebook']],
  ['none', 'Direct / app / QR', ['none']],
  ['google', 'Google', ['google']],
  ['instagram', 'Instagram', ['instagram']],
  ['other', 'Other sites', ['other']],
  ['search', 'Bing / other search', ['bing', 'other_search']],
];
const ALWAYS_SHOWN = new Set(['facebook', 'none', 'google']);

function sourceRows(v) {
  const add = (obj, keys) => keys.reduce((n, k) => n + ((obj && obj[k]) || 0), 0);
  return SOURCE_ROWS
    .map(([key, label, keys]) => ({ key, label, yesterday: add(v.yesterday.sources, keys), week: add(v.week.sources, keys) }))
    .filter((r) => ALWAYS_SHOWN.has(r.key) || r.week > 0)
    .sort((a, b) => b.week - a.week);
}

const TAG_LIMIT = 15;
function tagRows(v) {
  const week = v.week.tags || {};
  const yesterday = v.yesterday.tags || {};
  const special = ['invalid', 'other_tags'];
  const named = Object.keys(week).filter((k) => !special.includes(k)).sort((a, b) => week[b] - week[a]);
  const shown = named.slice(0, TAG_LIMIT);
  const label = { invalid: 'Invalid tags', other_tags: 'Other tags (over daily limit)' };
  const rows = [...shown, ...special.filter((k) => week[k] > 0)]
    .map((k) => ({ key: k, label: label[k] || k, yesterday: yesterday[k] || 0, week: week[k] || 0 }));
  return { rows, more: Math.max(0, named.length - TAG_LIMIT) };
}

export default function AdminRatingStats() {
  const { isFounder } = usePlatformClaims();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setStats(await call('ratingsAdminStats'));
    } catch (e) {
      setError(e?.message || 'Couldn\'t load stats.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isFounder) load(); }, [isFounder, load]);

  if (!isFounder) return <div className="p-6 text-sm text-zinc-600">Founder access only.</div>;

  const r = stats?.ratings;
  const c = stats?.clubs;
  const n = stats?.needsYou;
  const v = stats?.visits;
  const heldDetail = r && Object.entries(r.heldByReason || {}).map(([k, x]) => `${HELD_LABELS[k] || k} ${x}`).join(' · ');

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Ratings stats</h1>
          <p className="text-sm text-zinc-500">
            Scrimly Ratings · <Link to="/admin/ratings" className="text-lime-700 hover:underline">Moderation</Link>
            {' · '}<Link to="/admin/ratings/clubs" className="text-lime-700 hover:underline">Club directory</Link>
          </p>
        </div>
        <button onClick={load} disabled={loading} className="p-2 rounded-lg hover:bg-zinc-100 disabled:opacity-40" aria-label="Refresh">
          <ArrowPathIcon className={`w-5 h-5 text-zinc-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error} <button onClick={load} className="underline">Try again</button>
        </div>
      )}

      {!stats && loading && <div className="grid gap-4 sm:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="h-40 rounded-2xl bg-zinc-100 animate-pulse" />)}</div>}

      {stats && (
        <>
          <div className="grid gap-4 sm:grid-cols-3 mb-4">
            <Card title="Ratings">
              <Row label="Last 24 hours" value={r.last24h} />
              <Row label="This week" value={r.thisWeek} />
              <Row label="Total" value={r.total} />
              <Row label="Published" value={r.published} />
              <Row label="Waiting to appear" value={r.waiting} />
              <Row label="New accounts (7 days)" value={stats.newAccounts} />
            </Card>
            <Card title="Clubs">
              <Row label="Showing scores (5+)" value={c.showingScores} />
              <Row label="1 to 4 ratings" value={c.underThreshold} />
              <Row label="Active clubs" value={c.active} />
            </Card>
            <Card title="Needs you">
              <Row label="Comments waiting" value={n.commentsWaiting} warn={n.commentsWaiting > 0} />
              <Row label="Open reports" value={n.openReports} warn={n.openReports > 0} />
              <Row label="Held ratings" value={n.held} warn={n.held > 0} />
              {heldDetail && <p className="text-xs text-zinc-500 mt-1">{heldDetail}</p>}
              {(n.commentsWaiting > 0 || n.openReports > 0 || n.held > 0) && (
                <Link to="/admin/ratings" className="mt-2 inline-block text-sm text-lime-700 hover:underline">Open moderation ›</Link>
              )}
            </Card>
          </div>

          <Card title={v ? `Visits (${v.host})` : 'Visits'}>
            {!v ? (
              <p className="text-sm text-zinc-500">No visit counts yet. They start the day after Hosting logging is turned on.</p>
            ) : (
              <>
                <div className="grid grid-cols-[1fr_auto_auto] gap-x-6 text-sm">
                  <span className="text-xs uppercase tracking-wider text-zinc-400 py-1">Page</span>
                  <span className="text-xs uppercase tracking-wider text-zinc-400 py-1 text-right">{v.yesterday.date}</span>
                  <span className="text-xs uppercase tracking-wider text-zinc-400 py-1 text-right">{v.week.days} days</span>
                  {PAGE_ROWS.map(([key, label]) => (
                    <div key={key} className="contents">
                      <span className="text-zinc-600 py-1">{label}</span>
                      <span className="text-right tabular-nums font-semibold py-1">{v.yesterday.pages[key] || 0}</span>
                      <span className="text-right tabular-nums py-1">{v.week.pages[key] || 0}</span>
                    </div>
                  ))}
                </div>
                {v.topClubs.length > 0 && (
                  <p className="mt-3 text-sm text-zinc-600">
                    <span className="font-semibold text-zinc-800">Top club pages:</span>{' '}
                    {v.topClubs.map((t) => `${t.name} ${t.views}`).join(' · ')}
                  </p>
                )}
                <p className="mt-3 text-xs text-zinc-400">
                  Counted nightly from Hosting logs. Bots excluded. No individual visitors are tracked.
                  {v.partial ? ' Some days hit the counting limit, so totals are low.' : ''}
                </p>
              </>
            )}
          </Card>

          {v && (
            <div className="grid gap-4 sm:grid-cols-2 mt-4">
              <Card title="Where visits came from">
                <CountTable rows={sourceRows(v)} yesterdayLabel={v.yesterday.date} weekLabel={`${v.week.days} days`} />
                <p className="mt-3 text-xs text-zinc-400">
                  {v.sourcesSince ? `Recorded since ${v.sourcesSince}. ` : 'Not recorded yet. Starts with the next nightly count. '}
                  Moving between Ratings pages isn't counted as a new arrival.
                </p>
              </Card>
              <Card title="Tagged links (?from=)">
                {tagRows(v).rows.length === 0 ? (
                  <p className="text-sm text-zinc-500">No tags yet. Add ?from=name to a link to count it.</p>
                ) : (
                  <>
                    <CountTable heading="Tag" rows={tagRows(v).rows} yesterdayLabel={v.yesterday.date} weekLabel={`${v.week.days} days`} />
                    {tagRows(v).more > 0 && <p className="mt-2 text-xs text-zinc-500">and {tagRows(v).more} more</p>}
                  </>
                )}
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}
